/**
 * Debouncer purpose, flow and logic.
 *
 * The Debouncer's purpose is to delay execution of a particular function,
 * such as for example avoiding sending a request to the database to save
 * a piece of text if the user has not yet finished typing. E.g. every time
 * the user types again, the Debouncer will be used to further extend the
 * time until the request to save will be called.
 *
 * If you examine the function, when you call Debouncer(), it returns a function that can accept arguments
 * for earliestAllowedDateMS, threshold and func. This is called a Factory Function pattern. The most
 * valuable aspect of it here is being able to re-use the same function while getting a private context.
 * This allows us to set up many debounces across many contexts, without overriding any other debounce
 * and only overriding when we need to (such as if user starts typing again and we want to further postpone
 * saving their changes).
 *
 *
 * The flow of the Debouncer is the following:
 * 0. Variables and debounce functions get initialized.
 * 1. The Debouncer function first sets the earliest allowed time to call the function.
 * 2. Then a timeout is set that will use the executeIfAllowed function defined inside the debounce function
 * once the passed in threshold time passes to check if it's time to execute the function.
 * 3. Once the timeout hits the threshold, the executeIfAllowed will be called, which will check if the
 * earliest allowed time has arrived or not.
 * 4. If it's not yet allowed to execute the function, a new timeout will be set to try again in a second.
 * 5. If it is allowed, the initially passed in function will be called and the timeout will be cleared.
 *
 * Any time we want to postpone the execution of the initially passed in function further, we simply
 * re-use the saved reference (the returned debounce function) and call it again with the new, updated
 * earliest allowed time and threshold to make the update.
 */

function Debouncer() {
  /** Initialize Debouncer variables. */
  let allowedDateMS = 0; // Earliest allowed time for executing the function
  let timeout = null; // For tracking the timeout

  async function debounce(earliestAllowedDateMS, threshold, func) {
    /** Function checks whether time has passed to allow the passed in function to be executed. */
    async function executeIfAllowed() {
      /** Check if allowed time to execute function has arrived. */
      if (Date.now() < allowedDateMS) {
        /** Re-run function until allowed time arrives by resetting the current timeout
         * and setting the check to run again in 1 second. */
        timeout = setTimeout(executeIfAllowed, 1 * 1000);
      } else {
        /** If the threshold time has passed, execute the passed in function. */
        await func();

        /** Reset Debouncer to initial state as its purpose is served. */
        if (timeout) {
          clearTimeout(timeout);
          timeout = null;
        }
        allowedDateMS = 0;
      }
    }

    /** Set the earliest allowed time for executing the function. */
    allowedDateMS = earliestAllowedDateMS;

    /** Clear any previous running timeouts to ensure only the latest set timeout runs.
     * Skipped first time around since no timeout exists first time around. */
    if (timeout) {
      clearTimeout(timeout);
    }

    /** Set new timeout for executing the function. Only runs once every time
     * the debounce function is called. */
    timeout = setTimeout(executeIfAllowed, threshold);
  }

  return debounce;
}

module.exports = {
  Debouncer,
};
